import { apiCall } from './api';
import type {
  User,
  LoginRequest,
  LoginResponse,
  RegistrationRequest,
  RegistrationResponse,
  RefreshTokenResponse,
} from '@sulis-liiga/common';
import { API_ROUTES, STORAGE_KEYS } from '@sulis-liiga/common';

const authService = {
  /**
   * Log in a user with email and password
   */
  login: async (email: string, password: string, rememberMe = false): Promise<LoginResponse> => {
    const loginRequest: LoginRequest = { email, password, rememberMe };
    const response = await apiCall<LoginResponse>('post', API_ROUTES.AUTH.LOGIN, loginRequest);

    // Store tokens locally
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.user));

    return response;
  },

  /**
   * Register a new user
   */
  register: async (userData: RegistrationRequest): Promise<RegistrationResponse> => {
    return apiCall<RegistrationResponse>('post', API_ROUTES.AUTH.REGISTER, userData);
  },

  /**
   * Log out the current user
   */
  logout: async (): Promise<void> => {
    try {
      await apiCall<void>('post', API_ROUTES.AUTH.LOGOUT);
    } catch (error) {
      // Log the error but don't rethrow to ensure localStorage is cleared
      console.error('Logout API call failed:', error);
    }
    // Clear local storage regardless of API response
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
  },

  /**
   * Refresh the access token using a refresh token
   */
  refreshToken: async (): Promise<string> => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiCall<RefreshTokenResponse>('post', API_ROUTES.AUTH.REFRESH_TOKEN, {
      refreshToken,
    });

    // Store the new access token
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, response.accessToken);

    return response.accessToken;
  },

  /**
   * Request a password reset for a given email
   */
  requestPasswordReset: async (email: string): Promise<void> => {
    return apiCall<void>('post', '/v1/auth/password-reset/request', { email });
  },

  /**
   * Confirm password reset using a reset token
   */
  confirmPasswordReset: async (token: string, newPassword: string): Promise<void> => {
    return apiCall<void>('post', '/v1/auth/password-reset/confirm', {
      token,
      password: newPassword,
    });
  },

  /**
   * Verify email using verification token
   */
  verifyEmail: async (token: string): Promise<void> => {
    return apiCall<void>('get', `/v1/auth/verify-email/${token}`);
  },

  /**
   * Resend the email-verification message for an unverified account
   */
  resendVerification: async (email: string): Promise<void> => {
    return apiCall<void>('post', '/v1/auth/resend-verification', { email });
  },

  /**
   * Change password for the currently logged in user
   */
  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    return apiCall<void>('post', API_ROUTES.USER.CHANGE_PASSWORD, {
      currentPassword,
      newPassword,
    });
  },

  /**
   * Get the current user profile
   */
  getCurrentUser: async (): Promise<User> => {
    return apiCall<User>('get', API_ROUTES.USER.PROFILE);
  },

  /**
   * Permanently erase the current user's account (GDPR right to be forgotten).
   * Local storage is cleared only after the server confirms erasure so a failed
   * request leaves the session intact.
   */
  deleteAccount: async (): Promise<void> => {
    await apiCall<void>('delete', API_ROUTES.USER.DELETE_ACCOUNT);
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
  },

  /**
   * Check if the user is authenticated based on local storage
   */
  isAuthenticated: (): boolean => {
    return !!localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  },

  /**
   * Get the current user from local storage
   */
  getStoredUser: (): User | null => {
    const userJson = localStorage.getItem(STORAGE_KEYS.USER);
    if (userJson) {
      try {
        return JSON.parse(userJson) as User;
      } catch (error) {
        console.error('Failed to parse stored user', error);
        return null;
      }
    }
    return null;
  },
};

export default authService;
